import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useWeb3 } from '../context/Web3Context';
import api from '../utils/api';
import { toast } from 'react-toastify';
import './ProductDetailModal.css';

// ERC20 ABI
const ERC20_ABI = [
  {
    constant: true,
    inputs: [{ name: '_owner', type: 'address' }],
    name: 'balanceOf',
    outputs: [{ name: 'balance', type: 'uint256' }],
    type: 'function'
  },
  {
    constant: false,
    inputs: [
      { name: '_spender', type: 'address' },
      { name: '_value', type: 'uint256' }
    ],
    name: 'approve',
    outputs: [{ name: '', type: 'bool' }],
    type: 'function'
  },
  {
    constant: true,
    inputs: [
      { name: '_owner', type: 'address' },
      { name: '_spender', type: 'address' }
    ],
    name: 'allowance',
    outputs: [{ name: '', type: 'uint256' }],
    type: 'function'
  },
  {
    constant: false,
    inputs: [
      { name: '_to', type: 'address' },
      { name: '_value', type: 'uint256' }
    ],
    name: 'transfer',
    outputs: [{ name: '', type: 'bool' }],
    type: 'function'
  }
];

const ProductDetailModal = ({ product, isOpen, onClose, onPurchaseComplete }) => {
  const { account, isConnected, web3 } = useWeb3();
  const [selectedPreview, setSelectedPreview] = useState(null);
  const [isPlaying, setIsPlaying] = useState(false);
  const [currentTime, setCurrentTime] = useState(0);
  const [duration, setDuration] = useState(0);
  const [purchasing, setPurchasing] = useState(false);
  const [approving, setApproving] = useState(false);
  const [usdtApproved, setUsdtApproved] = useState(false);
  const audioElementRef = useRef(null);

  // USDT contract address on BSC Mainnet
  const USDT_ADDRESS = process.env.REACT_APP_USDT_ADDRESS || '0x55d398326f99059fF775485246999027B3197955';
  const PHONE_TOKEN_ADDRESS = process.env.REACT_APP_PHONE_TOKEN_ADDRESS || '';
  
  // Payment wallet address (where payments are sent - should be set in env)
  const PAYMENT_WALLET_ADDRESS = process.env.REACT_APP_PAYMENT_WALLET_ADDRESS || process.env.REACT_APP_ADMIN_WALLET_ADDRESS || '';

  useEffect(() => {
    if (!isOpen) {
      setSelectedPreview(null);
      setIsPlaying(false);
      if (audioElementRef.current) {
        audioElementRef.current.pause();
        audioElementRef.current.currentTime = 0;
      }
    }
  }, [isOpen]);

  const checkUSDTApproval = useCallback(async () => {
    if (!account || !product || !web3 || !PAYMENT_WALLET_ADDRESS) {
      setUsdtApproved(false);
      return;
    }

    try {
      const tokenAddress = product.paymentCurrency === 'USDT' ? USDT_ADDRESS : PHONE_TOKEN_ADDRESS;
      if (!tokenAddress) {
        setUsdtApproved(false);
        return;
      }

      const tokenContract = new web3.eth.Contract(ERC20_ABI, tokenAddress);
      const priceInWei = web3.utils.toWei(product.price.toString(), 'ether');
      const allowance = await tokenContract.methods.allowance(account, PAYMENT_WALLET_ADDRESS).call();
      
      const allowanceBigInt = BigInt(allowance.toString());
      const priceBigInt = BigInt(priceInWei);
      setUsdtApproved(allowanceBigInt >= priceBigInt);
    } catch (error) {
      console.error('Error checking approval:', error);
      setUsdtApproved(false);
    }
  }, [account, product, web3, PAYMENT_WALLET_ADDRESS, USDT_ADDRESS, PHONE_TOKEN_ADDRESS]);

  useEffect(() => {
    checkUSDTApproval();
  }, [checkUSDTApproval, isOpen]);

  const handlePreviewClick = (previewFile) => {
    if (selectedPreview?._id === previewFile._id && isPlaying) {
      // Pause if same track is playing
      if (audioElementRef.current) {
        audioElementRef.current.pause();
        setIsPlaying(false);
      }
    } else {
      // Play new track
      setSelectedPreview(previewFile);
      setIsPlaying(true);
      // Use relative URL - works on both localhost and production
      const previewUrl = `/api/products/${product._id}/preview/${previewFile._id}`;
      if (audioElementRef.current) {
        audioElementRef.current.src = previewUrl;
        audioElementRef.current.play();
      }
    }
  };

  const handleAudioTimeUpdate = () => {
    if (audioElementRef.current) {
      setCurrentTime(audioElementRef.current.currentTime);
      setDuration(audioElementRef.current.duration || 0);
    }
  };

  const handlePlayPause = () => {
    if (audioElementRef.current) {
      if (isPlaying) {
        audioElementRef.current.pause();
      } else {
        audioElementRef.current.play();
      }
      setIsPlaying(!isPlaying);
    }
  };

  const handleApproveUSDT = async () => {
    if (!account || !web3) {
      toast.error('Please connect your wallet');
      return;
    }

    setApproving(true);
    try {
      const tokenAddress = product.paymentCurrency === 'USDT' ? USDT_ADDRESS : PHONE_TOKEN_ADDRESS;
      if (!tokenAddress) {
        toast.error(`${product.paymentCurrency} token contract not configured`);
        return;
      }

      const tokenContract = new web3.eth.Contract(ERC20_ABI, tokenAddress);
      const maxApproval = BigInt(web3.utils.toWei('10000', 'ether')); // Approve 10,000 tokens

      await tokenContract.methods.approve(PAYMENT_WALLET_ADDRESS, maxApproval.toString()).send({ from: account });
      
      toast.success(`${product.paymentCurrency} approved successfully!`);
      await new Promise(resolve => setTimeout(resolve, 2000));
      
      // Verify approval
      let verified = false;
      for (let i = 0; i < 5; i++) {
        const allowance = await tokenContract.methods.allowance(account, PAYMENT_WALLET_ADDRESS).call();
        const priceInWei = web3.utils.toWei(product.price.toString(), 'ether');
        const allowanceBigInt = BigInt(allowance.toString());
        const priceBigInt = BigInt(priceInWei);
        if (allowanceBigInt >= priceBigInt) {
          setUsdtApproved(true);
          verified = true;
          break;
        }
        if (i < 4) await new Promise(resolve => setTimeout(resolve, 2000));
      }
      if (!verified) {
        console.warn('Approval not verified but transaction succeeded');
        setUsdtApproved(true);
      }
    } catch (error) {
      console.error('Approval error:', error);
      if (error.code === 4001) {
        toast.error('Transaction rejected by user');
      } else {
        toast.error(`Failed to approve ${product.paymentCurrency}`);
      }
    } finally {
      setApproving(false);
    }
  };

  const handlePurchase = async () => {
    if (!account || !web3 || !isConnected) {
      toast.error('Please connect your wallet');
      return;
    }

    if (!PAYMENT_WALLET_ADDRESS) {
      toast.error('Payment wallet address not configured. Please contact administrator.');
      return;
    }

    if (!usdtApproved && product.paymentCurrency === 'USDT') {
      toast.error('Please approve USDT first');
      return;
    }

    setPurchasing(true);
    try {
      const tokenAddress = product.paymentCurrency === 'USDT' ? USDT_ADDRESS : PHONE_TOKEN_ADDRESS;
      if (!tokenAddress) {
        toast.error(`${product.paymentCurrency} token contract not configured`);
        return;
      }

      const tokenContract = new web3.eth.Contract(ERC20_ABI, tokenAddress);
      const priceInWei = web3.utils.toWei(product.price.toString(), 'ether');

      // Transfer tokens to payment wallet
      const tx = await tokenContract.methods.transfer(PAYMENT_WALLET_ADDRESS, priceInWei.toString()).send({ from: account });
      
      toast.success('Payment successful! Verifying purchase...');

      // Verify purchase on backend
      await api.post('/purchases/verify', {
        productId: product._id,
        transactionHash: tx.transactionHash,
        paymentCurrency: product.paymentCurrency
      });

      toast.success('Purchase completed successfully!');
      if (onPurchaseComplete) {
        onPurchaseComplete();
      }
      onClose();
    } catch (error) {
      console.error('Purchase error:', error);
      if (error.code === 4001) {
        toast.error('Transaction rejected by user');
      } else if (error.response?.data?.message) {
        toast.error(error.response.data.message);
      } else {
        toast.error('Purchase failed. Please try again.');
      }
    } finally {
      setPurchasing(false);
    }
  };

  const formatTime = (seconds) => {
    if (!seconds || isNaN(seconds)) return '0:00';
    const mins = Math.floor(seconds / 60);
    const secs = Math.floor(seconds % 60);
    return `${mins}:${secs.toString().padStart(2, '0')}`;
  };

  if (!isOpen || !product) return null;

  return (
    <div className="product-detail-modal-overlay" onClick={onClose}>
      <div className="product-detail-modal" onClick={(e) => e.stopPropagation()}>
        <button className="modal-close-btn" onClick={onClose}>×</button>
        
        <div className="modal-header">
          {product.image && (
            <img src={product.image} alt={product.title} className="modal-product-image" />
          )}
          <div className="modal-product-info">
            <h2>{product.title}</h2>
            <p className="modal-product-category">{product.category.replace('_', ' ')}</p>
            <p className="modal-product-description">{product.description}</p>
            <div className="modal-price-section">
              <span className="modal-price">{product.price} {product.paymentCurrency || product.currency}</span>
              {product.files && product.files.length > 0 && (
                <span className="modal-file-count">{product.files.length} {product.files.length === 1 ? 'track' : 'tracks'}</span>
              )}
            </div>
          </div>
        </div>

        {/* Preview Section */}
        {product.previewFiles && product.previewFiles.length > 0 && (
          <div className="modal-preview-section">
            <h3>Preview Tracks</h3>
            <div className="preview-list">
              {product.previewFiles.map((preview, index) => (
                <div 
                  key={preview._id || index} 
                  className={`preview-item ${selectedPreview?._id === preview._id ? 'active' : ''}`}
                  onClick={() => handlePreviewClick(preview)}
                >
                  <div className="preview-track-number">{index + 1}</div>
                  <div className="preview-track-info">
                    <div className="preview-track-name">{preview.originalName || `Track ${index + 1}`}</div>
                    {selectedPreview?._id === preview._id && (
                      <div className="preview-player">
                        <audio
                          ref={audioElementRef}
                          onTimeUpdate={handleAudioTimeUpdate}
                          onLoadedMetadata={handleAudioTimeUpdate}
                          onEnded={() => setIsPlaying(false)}
                          style={{ display: 'none' }}
                        />
                        <button className="play-pause-btn" onClick={(e) => { e.stopPropagation(); handlePlayPause(); }}>
                          {isPlaying ? '⏸' : '▶'}
                        </button>
                        <div className="audio-progress">
                          <div className="audio-progress-bar">
                            <div 
                              className="audio-progress-fill" 
                              style={{ width: duration > 0 ? `${(currentTime / duration) * 100}%` : '0%' }}
                            />
                          </div>
                          <div className="audio-time">
                            <span>{formatTime(currentTime)}</span>
                            <span>/</span>
                            <span>{formatTime(duration)}</span>
                          </div>
                        </div>
                      </div>
                    )}
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Purchase Section */}
        <div className="modal-purchase-section">
          {!isConnected ? (
            <div className="purchase-message">
              <p>Please connect your wallet to purchase this product</p>
            </div>
          ) : !usdtApproved ? (
            <div className="purchase-actions">
              <button 
                className="btn btn-primary btn-approve"
                onClick={handleApproveUSDT}
                disabled={approving}
              >
                {approving ? 'Approving...' : `Approve ${product.paymentCurrency}`}
              </button>
            </div>
          ) : (
            <div className="purchase-actions">
              <button 
                className="btn btn-primary btn-purchase"
                onClick={handlePurchase}
                disabled={purchasing}
              >
                {purchasing ? 'Processing...' : `Purchase for ${product.price} ${product.paymentCurrency || product.currency}`}
              </button>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default ProductDetailModal;

